import { IApiError } from '@frontend/request';

import { ESymbolCategory, ISymbol, marketAPIController } from '@aqx-core/common/api/market';
import { EWatchlistCategory, watchlistAPIController } from '@aqx-core/common/api/watchlist';
import { mapResponseCodeToMessage } from '@aqx-core/common/request/message';

import { tradeAnalysisViewController } from '../trade/analysis/controller';
import { tradeWatchlistViewController } from '../trade/watchlist/controller';
import { searchService } from './service';

class SearchViewController {
  private searchSymbolResult: ISymbol[] = [];
  private selectedSymbolCategory: ESymbolCategory = ESymbolCategory.ALL;
  private rawSearchHistories: ISymbol[] = [];
  private filteredSearchHistories: ISymbol[] = [];
  private favouriteSymbolIds: string[] = [];
  private isNavigatingFromSearch: boolean = false;

  public consumeIsNavigatingFromSearch = () => {
    const value = this.isNavigatingFromSearch;
    this.isNavigatingFromSearch = false;
    return value;
  };

  public filterSearchSymbolAndHistoryByCategory = (category: ESymbolCategory) => {
    this.setSymbolCategoryWithAll(category);
    this.filterSearchSymbolByCategory(category, this.searchSymbolResult);
    this.filterSearchHistoriesByCategory(category, this.rawSearchHistories);
  };

  public onSelectSearchResult = (symbol: string, symbolObject: ISymbol) => {
    this.isNavigatingFromSearch = true;
    this.updateSearchHistories(symbol);
    tradeWatchlistViewController.onWatchlistFilterChanged(EWatchlistCategory.ALL);
    tradeWatchlistViewController.onAllSymbolCategoryChanged(undefined);
    tradeAnalysisViewController.setSymbolDetail({ symbolId: symbol, symbol: symbolObject });
  };

  /** Search symbol */
  public setSymbolSearching = (nextSearching: boolean) => {
    searchService.setSearching(nextSearching);
  };

  public clearSearchResult = () => {
    this.setSymbolSearching(false);
    this.searchSymbolResult = [];
    this.setSymbolCategoryWithAll(ESymbolCategory.ALL);
    searchService.setSearchSymbolResult([]);
    this.filterSearchHistoriesByCategory(ESymbolCategory.ALL, this.rawSearchHistories);
  };

  public getSearchSymbols = async (text: string) => {
    this.setSymbolSearching(true);
    await marketAPIController.searchSymbol(
      { text },
      {
        onSuccess: (searchSymbols) => {
          const dataSearchSymbols = searchSymbols || [];
          this.setSymbolSearching(false);
          this.searchSymbolResult = dataSearchSymbols;
          this.filterSearchSymbolAndHistoryByCategory(this.selectedSymbolCategory);

          this.favouriteSymbolIds = [
            ...new Set([
              ...this.favouriteSymbolIds,
              ...dataSearchSymbols.filter((symbol) => symbol.watch).map((symbol) => symbol.symbol)
            ])
          ];
          searchService.setFavouriteSymbolIds(this.favouriteSymbolIds);
        },
        onError: (error) => {
          this.setSymbolSearching(false);
          const { code, message } = error as IApiError;
          throw new Error(mapResponseCodeToMessage(code, message));
        }
      }
    );
  };

  public updateSearchSymbolResult = (symbolId: string, value: Partial<ISymbol>) => {
    const searchSymbolResult = searchService.context.searchSymbolResult.map((symbol) => {
      if (symbol.symbol === symbolId) {
        return { ...symbol, ...value };
      }
      return symbol;
    });

    searchService.setSearchSymbolResult(searchSymbolResult);
    this.searchSymbolResult = searchSymbolResult;
  };

  public updateFavouriteSymbolList = (symbolId: string, value: boolean) => {
    if (value) {
      this.favouriteSymbolIds = [...new Set([...this.favouriteSymbolIds, symbolId])];
    } else {
      this.favouriteSymbolIds = this.favouriteSymbolIds.filter((id) => id !== symbolId);
    }
    searchService.setFavouriteSymbolIds(this.favouriteSymbolIds);
  };

  public handleFavouriteSymbolInSearch = async (symbolId: string, value: boolean) => {
    this.updateFavouriteSymbolList(symbolId, value);
    await tradeWatchlistViewController.onFavouriteSymbol(symbolId, value);
  };

  /** Search history */
  public getSearchHistories = async () => {
    marketAPIController.getSearchHistories({
      onSuccess: (searchHistories = []) => {
        const dataSearchHistories = searchHistories || [];

        this.rawSearchHistories = dataSearchHistories;
        this.filteredSearchHistories = dataSearchHistories;
        searchService.setRawSearchHistories(dataSearchHistories);
        searchService.setFilteredSearchHistories(dataSearchHistories);

        this.favouriteSymbolIds = [
          ...new Set([
            ...this.favouriteSymbolIds,
            ...dataSearchHistories.filter((symbol) => symbol.watch).map((symbol) => symbol.symbol)
          ])
        ];
        searchService.setFavouriteSymbolIds(this.favouriteSymbolIds);
      },
      onError: (error) => {
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public updateSearchHistories = async (text: string) => {
    marketAPIController.updateSearchHistories(
      { text },
      {
        onSuccess: () => {
          this.getSearchHistories();
        },
        onError: (error) => {
          const { code, message } = error as IApiError;
          throw new Error(mapResponseCodeToMessage(code, message));
        }
      }
    );
  };

  /** If selected tab is All, delete all history , else delete only the filtered history */
  public deleteSymbolHistories = async () => {
    const symbolIds = this.filteredSearchHistories.map((symbol) => symbol.symbol);
    if (this.selectedSymbolCategory !== ESymbolCategory.ALL && symbolIds && symbolIds.length > 0) {
      this.deleteIndividualSymbolHistory(symbolIds);
    } else {
      this.deleteSymbolHistoriesAll();
    }
  };

  /** To ask BE if can accept string[] for multiple delete */
  private deleteIndividualSymbolHistory = async (symbolIds: string[]) => {
    searchService.setDeleting(true);

    const deletePromises = symbolIds.map((symbolId) =>
      marketAPIController.deleteSearchHistories(
        { symbol: symbolId },
        {
          onSuccess: () => {
            this.removeSearchHistory(symbolId);
          },
          onError: (error) => {
            const { code, message } = error as IApiError;
            throw new Error(mapResponseCodeToMessage(code, message));
          }
        }
      )
    );

    try {
      await Promise.all(deletePromises);
    } catch (error) {
      throw error;
    } finally {
      searchService.setDeleting(false);
    }
  };

  private deleteSymbolHistoriesAll = async () => {
    /** Remove from UI first UI */
    searchService.setDeleting(true);
    this.resetSearchHistories();
    /** Pass undefined to delete all symbol history */
    marketAPIController.deleteSearchHistories(undefined, {
      onSuccess: () => {
        searchService.setDeleting(false);
        this.resetSearchHistories();
      },
      onError: (error) => {
        searchService.setDeleting(false);
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  /** Private function */
  private setSymbolCategoryWithAll = (categroy: ESymbolCategory) => {
    searchService.setSelectedSymbolCategory(categroy);
    this.selectedSymbolCategory = categroy;
  };

  private filterSearchSymbolByCategory = (category: ESymbolCategory, symbols: ISymbol[]) => {
    if (category === ESymbolCategory.ALL) {
      searchService.setSearchSymbolResult(symbols);
      return;
    }
    searchService.setSearchSymbolResult(symbols.filter((symbol) => symbol.type === category));
    return;
  };

  private filterSearchHistoriesByCategory = (category: ESymbolCategory, searchHistories: ISymbol[]) => {
    if (category === ESymbolCategory.ALL) {
      searchService.setRawSearchHistories(searchHistories);
      this.filteredSearchHistories = searchHistories;
      searchService.setFilteredSearchHistories(searchHistories);
      return;
    }

    const filteredSearchHistories = searchHistories.filter((symbol) => symbol.type === category);
    this.filteredSearchHistories = filteredSearchHistories;
    searchService.setFilteredSearchHistories(filteredSearchHistories);
    return;
  };

  private resetSearchHistories = () => {
    this.rawSearchHistories = [];
    this.filteredSearchHistories = [];
    searchService.setRawSearchHistories([]);
    searchService.setFilteredSearchHistories([]);
  };

  private removeSearchHistory = (symbolId: string) => {
    this.rawSearchHistories = this.rawSearchHistories.filter((symbol) => symbol.symbol !== symbolId);
    this.filteredSearchHistories = this.filteredSearchHistories.filter((symbol) => symbol.symbol !== symbolId);
    searchService.setRawSearchHistories(this.rawSearchHistories);
    searchService.setFilteredSearchHistories(this.filteredSearchHistories);
  };

  /**
   * Initialize favorites list from server on app startup
   * This ensures Search Service has the correct favorites list even before user searches
   */
  public initializeFavourites = async () => {
    await watchlistAPIController.getWatchlistWithoutMiniChart(
      { code: EWatchlistCategory.FAVOURITES },
      {
        onSuccess: (symbols) => {
          if (symbols && symbols.length > 0) {
            this.favouriteSymbolIds = symbols.map((symbol) => symbol.symbol);
            searchService.setFavouriteSymbolIds(this.favouriteSymbolIds);
          }
        },
        onError: () => {
          // Silently fail for initialization - favorites will be populated during search operations
        }
      }
    );
  };
}

export const searchViewController = new SearchViewController();
